import { type ColorScale, definePreset } from '@primeuix/themes';
import { Preset } from '@primeuix/themes/types';

import * as components from './components/index.js';
import { createSemanticPreset } from './semantics/index.js';
import * as a from './tokens/aliases.js';
import { type AliasSet, type SemanticOverrides, createSemanticTokens } from './tokens/semantic.js';

export type { SemanticOverrides } from './tokens/semantic.js';
export { brandPalette } from './brand-palette.js';
export { rentsodyBlue } from './tokens/palettes.js';
export { nbxBrandOverrides, type NbxBrandPalettes } from './nbx-brand.js';

/**
 * PrimeUI (PrimeNG 22+) commercial license token.
 *
 * Passed to `providePrimeNG({ license: PRIMENG_LICENSE })` in every app so the runtime license
 * check passes and the "Invalid PrimeUI License" banner is not shown. The token is verified
 * client-side (Ed25519) and ships in the browser bundle, so it is not a server secret.
 *
 */
export const PRIMENG_LICENSE =
  'eyJpZCI6IjBiMjdlMjljLTNjMzAtNDg0Zi1hNDkyLTczNGNlMmU4YzFmYSIsInByb2R1Y3QiOiJwcmltZXVpIiwidGllciI6ImNvbW11bml0eSIsInR5cGUiOiJkZXYiLCJpYXQiOjE3ODI5OTcyNzUsImV4cCI6MTgxNDUzMzI3NX0.4OU7XteT1MbCMRg2eqGUuG19XCjxX3PNKiQeB0nDBopUMcx1YrJps_PbSF11MRzv4GHYS1E7YL-bkU4nFjWaAw';

/** Palette references the preset exposes as PrimeNG primitives, all mode-aware NBX variables. */
const NBX_ALIASES: AliasSet = {
  primary: a.primary,
  secondary: a.secondary,
  surface: a.surface,
  success: a.success,
  warning: a.warning,
  danger: a.danger,
};

/**
 * NBX Design System preset for PrimeNG. Every primitive, semantic and component token is a
 * `var(--nbx-…)` reference to the generated NBX custom properties, so PrimeNG's `--p-*`
 * variables follow the NBX catalog and the active mode: switching
 * `<html data-nbx-mode="rentals|ssr|lock">` re-themes the components without `updatePreset`.
 * The `--nbx-*` stylesheet (`@nbx/styles/tokens/generated/nbx-tokens.css`) must be loaded.
 */
export function createNbxPreset(semanticOverrides?: SemanticOverrides): Preset {
  const al = NBX_ALIASES;
  const s = createSemanticTokens(semanticOverrides);

  return definePreset({
    primitive: {
      primary: { ...al.primary },
      secondary: { ...al.secondary },
      success: { ...al.success },
      warning: { ...al.warning },
      danger: { ...al.danger },
      surface: { ...al.surface },
      lock: { ...a.lock },
      home: { ...a.home },
      guest: { ...a.guest },
      rentsody: { ...a.rentsody },
      rentals: { ...a.rentals },
    },
    semantic: createSemanticPreset(s, al),
    components: {
      accordion: components.createAccordion(s),
      autocomplete: components.createAutocomplete(s),
      badge: components.createBadge(s),
      breadcrumb: components.createBreadcrumb(s),
      button: components.createButton(s),
      card: components.createCard(s),
      checkbox: components.createCheckbox(s),
      confirmdialog: components.createConfirmdialog(s),
      datatable: components.createDatatable(s),
      datepicker: components.createDatepicker(s),
      dialog: components.createDialog(s),
      iconfield: components.createIconfield(s),
      inputgroup: components.createInputgroup(s),
      inputnumber: components.createInputnumber(s),
      inputtext: components.createInputtext(s),
      menu: components.createMenu(s),
      message: components.createMessage(s),
      overlaybadge: components.createOverlaybadge(s),
      multiselect: components.createMultiselect(s),
      paginator: components.createPaginator(s),
      popover: components.createPopover(s),
      rating: components.createRating(s),
      select: components.createSelect(s),
      selectbutton: components.createSelectbutton(s),
      steps: components.createSteps(s),
      tabs: components.createTabs(s),
      tag: components.createTag(s),
      textarea: components.createTextarea(s),
      toast: components.createToast(s),
      togglebutton: components.createTogglebutton(s),
      toolbar: components.createToolbar(s),
      tooltip: components.createTooltip(s),
    },
  });
}

/**
 * @deprecated Use `createNbxPreset()` and `NbxThemeStore.setBrand()`. Legacy palette arguments
 * override NBX custom properties in the returned preset; semantic overrides still reach PrimeNG.
 */
export function createRentsodyPreset(
  primary?: ColorScale,
  secondary?: ColorScale,
  surface?: ColorScale,
  success?: ColorScale,
  warning?: ColorScale,
  danger?: ColorScale,
  semanticOverrides?: SemanticOverrides
): Preset {
  const preset = createNbxPreset(semanticOverrides);
  const palettes: [string, ColorScale | undefined][] = [
    ['primary', primary],
    ['secondary', secondary],
    ['neutral', surface],
    ['success', success],
    ['warning', warning],
    ['danger', danger],
  ];
  const declarations = palettes.flatMap(([role, scale]) =>
    Object.entries(scale ?? {}).flatMap(([shade, value]) =>
      /^(50|[1-9]00|950)$/.test(shade) && typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value)
        ? [`--nbx-color-${role}-${shade}: ${value.toLowerCase()}`]
        : []
    )
  );
  return declarations.length
    ? Object.assign(preset, { css: `:root { ${declarations.join('; ')}; }` })
    : preset;
}

export { createRentsodyPreset as RentsodyPreset };
